// src/components/Separation/TerminationModal.tsx

import React, { useEffect, useRef, useState } from 'react';
import { toast } from "sonner";
import { ChevronDown, Search } from 'lucide-react';
import { TerminationCreate } from '../../types/separation';
import { terminateEmployee } from '../../api/separation';
import { getEmployees } from '../../api/employees';

interface TerminationModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId?: string;
  employeeName?: string;
  onSuccess: () => void;
}

// Shape of a row from /employeesAll — `id` is the Employee.id that
// POST /separation/terminate expects (NOT user_id).
interface EmployeeLite {
  id: string;
  user_id?: string;
  employeeID?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  designation?: string;
}

const empName = (e: EmployeeLite) =>
  [e.first_name, e.last_name].filter(Boolean).join(' ').trim() || e.email || e.id;

const empLabel = (e: EmployeeLite) =>
  `${empName(e)}${e.employeeID ? ` (${e.employeeID})` : ''}`;

const blankForm = (employeeId?: string): TerminationCreate => ({
  employee_id: employeeId || '',
  termination_date: new Date().toISOString().split('T')[0],
  last_working_day: '',
  termination_type: 'OTHER',
  reason: '',
  additional_notes: '',
  notice_served: false,
  notice_waived: false,
  notice_pay_in_lieu: false,
  show_cause_issued: false,
  show_cause_response: '',
  eligible_for_rehire: true,
});

const TerminationModal: React.FC<TerminationModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  employeeName = 'Employee',
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<TerminationCreate>(() => blankForm(employeeId));

  // Employee pick-list. Only needed when the caller didn't pre-select someone —
  // the Terminations tab opens this modal with no employee at all.
  const [employees, setEmployees] = useState<EmployeeLite[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeQuery, setEmployeeQuery] = useState('');
  // Held as an object rather than looked up in `employees`: a later search can
  // drop the picked row from the list without losing who was selected.
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeLite | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const needsPicker = !employeeId;

  // Start from a clean form every time the modal opens — otherwise the fields
  // (and the previously picked employee) survive from the last termination.
  useEffect(() => {
    if (!isOpen) return;
    setFormData(blankForm(employeeId));
    setEmployeeQuery('');
    setSelectedEmployee(null);
    setPickerOpen(false);
  }, [isOpen, employeeId]);

  // Close the dropdown on an outside click or Escape, the way a native select
  // behaves — without this it would sit open over the date fields.
  useEffect(() => {
    if (!pickerOpen) return;
    const onPointerDown = (ev: MouseEvent) => {
      if (!pickerRef.current?.contains(ev.target as Node)) setPickerOpen(false);
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pickerOpen]);

  // Search runs server-side because /employeesAll caps at 100 rows — a client-only
  // filter would hide everyone past that cap in a large org. Debounced so typing
  // isn't a fetch per keystroke. `active_only` drops inactive/terminated staff, so
  // the backend's "employee already has an active termination record" guard can't
  // be tripped from the picker.
  useEffect(() => {
    if (!isOpen || !needsPicker) return;
    let cancelled = false;
    const q = employeeQuery.trim();
    const timer = setTimeout(() => {
      setEmployeesLoading(true);
      getEmployees({ limit: 100, active_only: true, search: q || undefined })
        .then((res) => {
          if (!cancelled) setEmployees(Array.isArray(res?.employees) ? res.employees : []);
        })
        .catch(() => {
          if (!cancelled) setEmployees([]);
        })
        .finally(() => {
          if (!cancelled) setEmployeesLoading(false);
        });
    }, q ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, needsPicker, employeeQuery]);

  // Blank while the picker is still empty — the trigger below already says
  // "Select employee", so an "Employee: Not selected" subtitle is just noise.
  const headerName = selectedEmployee ? empLabel(selectedEmployee) : (employeeName || '').trim();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!formData.reason) {
      toast.error('Please provide a termination reason');
      return;
    }
    if (!formData.last_working_day) {
      toast.error('Please provide last working day');
      return;
    }

    setLoading(true);
    try {
      await terminateEmployee(formData);
      toast.success('Employee terminated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to terminate employee');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TerminationCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header and footer sit OUTSIDE the scroll area. As sticky children of
              it they were painted under the scrolling fields — the global theme
              positions form controls, and a z-index:auto sticky box loses that
              tie-break to later siblings. */}
          <div className="shrink-0 z-10 bg-white px-6 py-4 border-b border-gray-200 shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Terminate Employee</h3>
                {headerName && (
                  <p className="text-sm text-gray-500 mt-1">Employee: {headerName}</p>
                )}
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Employee picker — the Terminations tab opens this modal with no
                employee, so without this there is nothing to submit. */}
            {needsPicker && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee <span className="text-red-500">*</span>
                </label>

                {/* Collapsed trigger + dropdown panel: the full list inline pushed
                    the rest of the form under the sticky footer. */}
                <div className="relative" ref={pickerRef}>
                  <button
                    type="button"
                    onClick={() => setPickerOpen((open) => !open)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <span className={`truncate text-sm ${selectedEmployee ? 'text-gray-900' : 'text-gray-400'}`}>
                      {selectedEmployee ? empLabel(selectedEmployee) : 'Select employee'}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-gray-400 transition-transform ${pickerOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {pickerOpen && (
                    <div className="absolute left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                      <div className="relative p-2 border-b border-gray-100">
                        <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          autoFocus
                          type="text"
                          value={employeeQuery}
                          onChange={(e) => setEmployeeQuery(e.target.value)}
                          placeholder="Search by name, employee ID or email..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                        {employeesLoading ? (
                          <div className="px-4 py-6 text-center text-sm text-gray-500">Loading employees...</div>
                        ) : employees.length === 0 ? (
                          <div className="px-4 py-6 text-center text-sm text-gray-500">
                            {employeeQuery.trim() ? 'No employee matches your search' : 'No employees available'}
                          </div>
                        ) : (
                          employees.map((e) => {
                            const isSelected = e.id === formData.employee_id;
                            return (
                              <button
                                key={e.id}
                                type="button"
                                onClick={() => {
                                  setSelectedEmployee(e);
                                  handleInputChange('employee_id', e.id);
                                  setEmployeeQuery('');
                                  setPickerOpen(false);
                                }}
                                className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                                  isSelected ? 'bg-red-50' : ''
                                }`}
                              >
                                <div className="text-sm font-medium text-gray-900 truncate">{empName(e)}</div>
                                <div className="text-xs text-gray-400 truncate">
                                  {e.employeeID || e.email || `#${e.id.slice(0, 8)}`}
                                  {e.designation ? ` · ${e.designation}` : ''}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Termination Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Termination Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.termination_date}
                  onChange={(e) => handleInputChange('termination_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Working Day <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.last_working_day}
                  onChange={(e) => handleInputChange('last_working_day', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Termination Type <span className="text-red-500">*</span>
              </label>
              {/* Values must match the backend TerminationType enum, otherwise the
                  records list shows the raw code instead of a labelled badge. */}
              <select
                value={formData.termination_type}
                onChange={(e) => handleInputChange('termination_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="GROSS_MISCONDUCT">Gross Misconduct</option>
                <option value="PERFORMANCE">Performance</option>
                <option value="REDUNDANCY">Redundancy</option>
                <option value="CONTRACT_END">Contract End</option>
                <option value="VOLUNTARY">Voluntary</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Termination <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Provide detailed reason for termination..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
              <textarea
                value={formData.additional_notes}
                onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Any additional information..."
              />
            </div>

            {/* Notice Details */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-3">Notice Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_served}
                    onChange={(e) => handleInputChange('notice_served', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Notice Served</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_waived}
                    onChange={(e) => handleInputChange('notice_waived', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Notice Waived</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_pay_in_lieu}
                    onChange={(e) => handleInputChange('notice_pay_in_lieu', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Notice Pay in Lieu</span>
                </label>
              </div>
            </div>

            {/* Show Cause */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-3">Show Cause Details</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.show_cause_issued}
                    onChange={(e) => handleInputChange('show_cause_issued', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Show Cause Issued</span>
                </label>
                {formData.show_cause_issued && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show Cause Response</label>
                    <textarea
                      value={formData.show_cause_response}
                      onChange={(e) => handleInputChange('show_cause_response', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Employee's response to show cause notice..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Rehire Eligibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Eligible for Rehire
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.eligible_for_rehire === true}
                    onChange={() => handleInputChange('eligible_for_rehire', true)}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.eligible_for_rehire === false}
                    onChange={() => handleInputChange('eligible_for_rehire', false)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
            </div>
          </div>

          <div className="shrink-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Terminate Employee'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminationModal;